import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { userService } from '../../../_services';
import DateRangePicker from '@/components/ui/date-range-picker';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Pagination from '@/components/ui/pagination';
import { Loader2, Search, ArrowLeft, Copy, Check, Download } from 'lucide-react';

const PAGE_SIZE = 10;
const MAX_RANGE_DAYS = 7;

function truncateStr(str, max = 40) {
    return str && str.length > max ? `${str.slice(0, max)}…` : (str || '-');
}

// ─── Mobile card ─────────────────────────────────────────────────────────────
function UsageLogCard({ row, index, copiedIndex, onCopyParams }) {
    const params = row.requestParams || '';
    const isCopied = copiedIndex === index;
    const loggedAt = row.loggedAt ? row.loggedAt.replace('T', ' ') : '-';

    return (
        <Card>
            <CardContent className="p-4 space-y-3">
                <div className="min-w-0 border-b pb-3">
                    <p className="text-xs text-muted-foreground">Endpoint</p>
                    <p className="font-semibold text-sm text-primary break-words">{row.endpoint || '-'}</p>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div className="col-span-2">
                        <p className="text-xs text-muted-foreground">Full URL</p>
                        <p className="font-medium mt-0.5 text-primary break-all line-clamp-2">{row.fullUrl || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Actor</p>
                        <p className="font-medium mt-0.5 text-primary truncate">{row.actorContext || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Category</p>
                        <p className="font-medium mt-0.5 text-primary truncate">{row.actionCategory || '-'}</p>
                    </div>
                    <div className="col-span-2">
                        <p className="text-xs text-muted-foreground">Logged</p>
                        <p className="font-medium mt-0.5 text-primary/80">{loggedAt}</p>
                    </div>
                    <div className="col-span-2">
                        <p className="text-xs text-muted-foreground">Client IP</p>
                        <p className="font-medium mt-0.5 text-primary">{row.clientIp || '-'}</p>
                    </div>
                    {params ? (
                        <div className="col-span-2 pt-1">
                            <button
                                type="button"
                                className="flex items-center gap-1.5 text-left text-sm text-primary hover:text-accent transition-colors w-full"
                                onClick={() => onCopyParams(params, index)}
                            >
                                <span className="break-all line-clamp-3">{truncateStr(params, 120)}</span>
                                {isCopied
                                    ? <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                                    : <Copy className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                            </button>
                        </div>
                    ) : null}
                </div>
            </CardContent>
        </Card>
    );
}

function DashboardUsageReport({ onBack }) {
    const yesterday = moment().subtract(1, 'days');
    const today = moment();

    const [startDate, setStartDate] = useState(yesterday);
    const [endDate, setEndDate]     = useState(today);
    const [usageList, setUsageList]     = useState([]);
    const [usageListTemp, setUsageListTemp] = useState([]);
    const [pageOfItems, setPageOfItems] = useState([]);
    const [loader, setLoader]           = useState(false);
    const [searchText, setSearchText]   = useState('');
    const [dateError, setDateError]     = useState('');
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [userId, setUserId]           = useState('');
    const [userName, setUserName]       = useState('');

    useEffect(() => {
        try {
            const raw = window.atob(localStorage.getItem('ajs_user_id'));
            setUserId(raw.split(':')[0]);
            setUserName(raw.split(':')[1]);
        } catch {}
    }, []);

    useEffect(() => {
        if (userId) fetchUsageLogs(startDate, endDate);
    }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchUsageLogs = useCallback((from, to) => {
        setLoader(true);
        setUsageList([]);
        setUsageListTemp([]);
        setPageOfItems([]);
        setSearchText('');

        const fromStr = moment(from).format('YYYY-MM-DD');
        const toStr   = moment(to).format('YYYY-MM-DD');

        userService.GetUsageLogs(userId, userName, fromStr, toStr)
            .then(apidata => {
                setLoader(false);
                if (apidata && apidata.length > 0) {
                    setUsageList(apidata);
                    setUsageListTemp(apidata);
                } else {
                    setUsageList([]);
                    setUsageListTemp([]);
                }
            })
            .catch(() => setLoader(false));
    }, [userId, userName]);

    const handleCalendar = (e, picker) => {
        const s = picker.startDate;
        const en = picker.endDate;
        if (en.diff(s, 'days') > MAX_RANGE_DAYS) {
            setDateError(`Maximum ${MAX_RANGE_DAYS} days allowed. Please select a shorter range.`);
            return;
        }
        setDateError('');
        setStartDate(s);
        setEndDate(en);
        fetchUsageLogs(s, en);
    };

    const handleSearch = (e) => {
        const val = e.target.value;
        setSearchText(val);
        if (!val) {
            setUsageList(usageListTemp);
            return;
        }
        const s = val.toLowerCase();
        const filtered = usageListTemp.filter(r =>
            (r.endpoint       && r.endpoint.toLowerCase().includes(s)) ||
            (r.fullUrl        && r.fullUrl.toLowerCase().includes(s)) ||
            (r.actorContext   && r.actorContext.toLowerCase().includes(s)) ||
            (r.actionCategory && r.actionCategory.toLowerCase().includes(s)) ||
            (r.requestParams  && r.requestParams.toLowerCase().includes(s)) ||
            (r.clientIp       && r.clientIp.toLowerCase().includes(s))
        );
        setUsageList(filtered);
    };

    const handleCopyParams = (text, index) => {
        navigator.clipboard.writeText(text).catch(() => {});
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const handleDownloadCSV = () => {
        const headers = ['Endpoint', 'Full URL', 'Actor Context', 'Action Category', 'Request Params', 'Client IP', 'Logged At'];
        const rows = usageListTemp.map(r => [
            r.endpoint       || '',
            r.fullUrl        || '',
            r.actorContext   || '',
            r.actionCategory || '',
            r.requestParams  || '',
            r.clientIp       || '',
            r.loggedAt ? r.loggedAt.replace('T', ' ') : '',
        ].map(v => `"${v.replace(/"/g, '""')}"`).join(','));

        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DashboardUsageAnalytics_${moment().format('YYYYMMDD_HHmmss')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const truncate = (str, max = 40) =>
        str && str.length > max ? str.substring(0, max) + '…' : (str || '-');

    const displayRows = searchText ? usageList : pageOfItems;

    return (
        <div className="w-full space-y-6">
            <Button variant="outline" onClick={onBack} className="self-start">
                <ArrowLeft className="h-4 w-4" />
                Back to Reports
            </Button>

            <h1 className="text-3xl font-bold tracking-tight text-foreground">Dashboard Usage Analytics</h1>

            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-start">
                        <div className="relative flex-1 sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text"
                                className="pl-9 pr-9"
                                placeholder="Search endpoint, actor, IP…"
                                value={searchText}
                                onChange={handleSearch}
                                disabled={loader}
                            />
                        </div>

                        <div className="flex flex-col gap-1 sm:shrink-0">
                            <DateRangePicker
                                startDate={startDate}
                                endDate={endDate}
                                onApply={handleCalendar}
                                displayMode="unified"
                                maxDate={moment()}
                                disabled={loader}
                            />
                            {dateError && (
                                <p className="text-xs text-destructive">{dateError}</p>
                            )}
                        </div>

                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="shrink-0 w-full sm:w-auto"
                                        onClick={handleDownloadCSV}
                                        disabled={loader || usageListTemp.length === 0}
                                    >
                                        <Download className="h-4 w-4 sm:mr-2" />
                                        <span className="hidden sm:inline">Export CSV</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Download CSV</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </CardContent>
            </Card>

            {/* Desktop table */}
            <Card className="hidden md:block">
                <CardContent className="p-0">
                    {loader ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <Table className="table-fixed w-full">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="text-primary font-semibold w-[152px] max-w-[152px] min-w-0">Endpoint</TableHead>
                                            <TableHead className="text-primary font-semibold whitespace-nowrap">Full URL</TableHead>
                                            <TableHead className="text-primary font-semibold w-[128px] max-w-[128px] min-w-0">Actor Context</TableHead>
                                            <TableHead className="text-primary font-semibold whitespace-nowrap">Action Category</TableHead>
                                            <TableHead className="text-primary font-semibold w-[140px] max-w-[140px] min-w-0">Request Params</TableHead>
                                            <TableHead className="text-primary font-semibold w-[104px] max-w-[104px] min-w-0">Client IP</TableHead>
                                            <TableHead className="text-primary font-semibold whitespace-nowrap">Logged At</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {displayRows.length > 0 ? (
                                            displayRows.map((row, index) => {
                                                const params = row.requestParams || '';
                                                const isCopied = copiedIndex === index;
                                                const loggedAt = row.loggedAt ? row.loggedAt.replace('T', ' ') : '-';

                                                const CellWithTooltip = ({ value, maxLen = 35 }) => {
                                                    const short = truncate(value, maxLen);
                                                    const needsTip = value && value.length > maxLen;
                                                    return needsTip ? (
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <span className="cursor-default">{short}</span>
                                                                </TooltipTrigger>
                                                                <TooltipContent className="max-w-xs break-words">{value}</TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    ) : <span>{short}</span>;
                                                };

                                                const endpoint = row.endpoint;
                                                const endpointCell = endpoint ? (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <span className="block min-w-0 truncate cursor-default">{endpoint}</span>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-xs break-words">{endpoint}</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                ) : (
                                                    <span className="text-primary/70">-</span>
                                                );

                                                const actor = row.actorContext;
                                                const actorCell = actor ? (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <span className="block min-w-0 truncate cursor-default">{actor}</span>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-xs break-words">{actor}</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                ) : (
                                                    <span className="text-primary/70">-</span>
                                                );

                                                const clientIp = row.clientIp;
                                                const clientIpCell = clientIp ? (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <span className="block min-w-0 truncate cursor-default">{clientIp}</span>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-xs break-words">{clientIp}</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                ) : (
                                                    <span className="text-primary/70">-</span>
                                                );

                                                return (
                                                    <TableRow key={index} className="hover:bg-muted">
                                                        <TableCell className="text-sm text-primary w-[152px] max-w-[152px] min-w-0">
                                                            {endpointCell}
                                                        </TableCell>
                                                        <TableCell className="text-sm max-w-[160px] truncate text-primary">
                                                            <CellWithTooltip value={row.fullUrl} maxLen={30} />
                                                        </TableCell>
                                                        <TableCell className="text-sm text-primary w-[128px] max-w-[128px] min-w-0">
                                                            {actorCell}
                                                        </TableCell>
                                                        <TableCell className="text-sm text-primary">
                                                            <CellWithTooltip value={row.actionCategory} />
                                                        </TableCell>
                                                        <TableCell className="text-sm w-[140px] max-w-[140px] min-w-0 text-primary">
                                                            {params ? (
                                                                <TooltipProvider>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <button
                                                                                type="button"
                                                                                className="flex items-center gap-1.5 text-left hover:text-accent transition-colors max-w-full min-w-0"
                                                                                onClick={() => handleCopyParams(params, index)}
                                                                            >
                                                                                <span className="truncate min-w-0 flex-1">
                                                                                    {truncate(params, 30)}
                                                                                </span>
                                                                                {isCopied
                                                                                    ? <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                                                                                    : <Copy className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                                                                }
                                                                            </button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent className="max-w-xs break-words">
                                                                            {isCopied ? 'Copied!' : params}
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            ) : (
                                                                <span className="text-primary/70">-</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-sm text-primary w-[104px] max-w-[104px] min-w-0">
                                                            {clientIpCell}
                                                        </TableCell>
                                                        <TableCell className="text-sm whitespace-nowrap text-primary">
                                                            {loggedAt}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={7} className="text-center py-8 text-primary/70">
                                                    No records found
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                            {!searchText && usageList.length > 0 && (
                                <div className="p-4 border-t">
                                    <Pagination
                                        items={usageList}
                                        onChangePage={setPageOfItems}
                                        pageSize={PAGE_SIZE}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
                {!searchText && usageList.length > 0 && (
                    <Pagination
                        items={usageList}
                        onChangePage={setPageOfItems}
                        pageSize={PAGE_SIZE}
                        stickyTopOnMobile
                        className="px-2 py-2"
                    />
                )}

                {loader ? (
                    <Card>
                        <CardContent className="flex items-center justify-center p-4 py-12 sm:p-6 sm:py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </CardContent>
                    </Card>
                ) : displayRows.length > 0 ? (
                    displayRows.map((row, index) => (
                        <UsageLogCard
                            key={index}
                            row={row}
                            index={index}
                            copiedIndex={copiedIndex}
                            onCopyParams={handleCopyParams}
                        />
                    ))
                ) : (
                    <Card>
                        <CardContent className="text-center p-4 py-8 sm:p-6 sm:py-8 text-primary/70">
                            No records found
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}

export default DashboardUsageReport;
